import React from 'react';
import { createRoot } from 'react-dom/client';
import * as Sentry from '@sentry/react';
import App from './App.js';
// @font-face declarations only — no selectors, so this cannot reorder any page's
// cascade. Poppins is referenced from index.css (loaded on every route) but declared
// only here, so before the routes went lazy every page got it from the single
// extracted main.css; without this, Poppins silently falls back outside /feedback.
import './assets/fonts/stylesheet.css';

// Firefox throws on navigator.permissions.query({name: 'microphone'|'camera'}); degrade to a no-op instead.
if (navigator.permissions?.query) {
    const nativePermissionsQuery = navigator.permissions.query.bind(navigator.permissions);
    navigator.permissions.query = (descriptor) => {
        try {
            return nativePermissionsQuery(descriptor).catch((err) => {
                if (err instanceof TypeError) {
                    return { state: 'prompt', onchange: null };
                }
                throw err;
            });
        } catch (err) {
            if (err instanceof TypeError) {
                return Promise.resolve({ state: 'prompt', onchange: null });
            }
            throw err;
        }
    };
}

const envMode = process.env.REACT_APP_ENV;

const shouldEnableSentry = ['prod', 'test', 'dev'].includes(envMode);

let sentryInitialized = false;
let lastRestartAt = 0;

function restartSentrySession(reason) {
    if (!sentryInitialized) return;

    const now = Date.now();

    // Prevent restart spam (5s debounce)
    if (now - lastRestartAt < 5000) return;
    lastRestartAt = now;

    try {
        Sentry.endSession();
        Sentry.startSession();

        Sentry.setContext('session_restart', {
            reason: reason,
            timestamp: new Date().toISOString(),
        });

        Sentry.flush(5000);
        console.log('[Sentry] Session restarted:', reason);
    } catch (e) {
        console.warn('[Sentry] Failed to restart session', e);
    }
}

if (shouldEnableSentry) {
    Sentry.init({
        dsn: 'https://4ec628a04f79c8972323648566dd56eb@o4506863228616704.ingest.us.sentry.io/4511568711450624',

        release: process.env.REACT_APP_VERSION,
        environment: envMode,

        autoSessionTracking: true,

        integrations: [Sentry.browserTracingIntegration()],

        transport: Sentry.makeBrowserOfflineTransport(Sentry.makeFetchTransport),

        tracesSampleRate: envMode === 'prod' ? 0.1 : 1.0,

        tracePropagationTargets: ['localhost', /^https:\/\/.*\.incruiter\.com/],
        enableLogs: true,
        ignoreErrors: [
            'The fetching process for the media resource was aborted by the user agent',
            'signal is aborted without reason',
            'User denied permission to access capture device',
            "Cannot use 'in' operator to search for 'trackType' in undefined",
            'InvalidStateError: The object is in an invalid state.',
            /Converting circular structure to JSON[\s\S]*HTMLVideoElement/,
            'ServerErrors: peer not joined',
            'url.startsWith is not a function',
            'InvalidStateError: Peer connection is closed',
            'Error: [object Event]',
        ],
    });

    sentryInitialized = true;
    Sentry.setTag('env', envMode);

    window.addEventListener('online', function () {
        restartSentrySession('network_online');
    });

    document.addEventListener('visibilitychange', function () {
        if (document.visibilityState === 'visible') {
            restartSentrySession('tab_visible');
        }
    });
}

// After a new deploy, a browser with an already-loaded (or bfcache-restored) app shell can
// still request an old webpack chunk hash that no longer exists on the server. Because static
// asset requests here fall back to index.html (200) instead of a real 404, the browser tries to
// parse that HTML as the chunk's JS and throws a SyntaxError instead of a catchable chunk-load
// rejection. Reloading once picks up the current index.html with matching chunk hashes.
const CHUNK_RELOAD_STORAGE_KEY = 'stale-chunk-reload-at';
const CHUNK_RELOAD_COOLDOWN_MS = 60000;
window.addEventListener('error', (event) => {
    const isStaleChunkParseFailure =
        event.error instanceof SyntaxError &&
        typeof event.filename === 'string' &&
        /\/static\/js\/.+\.js(\?.*)?$/.test(event.filename) &&
        /unexpected token/i.test(event.error.message || '');
    if (!isStaleChunkParseFailure) return;

    const lastReloadAt = Number(sessionStorage.getItem(CHUNK_RELOAD_STORAGE_KEY) || 0);
    if (Date.now() - lastReloadAt < CHUNK_RELOAD_COOLDOWN_MS) return;
    sessionStorage.setItem(CHUNK_RELOAD_STORAGE_KEY, String(Date.now()));
    window.location.reload();
});

const root = createRoot(document.getElementById('root'));
root.render(
    <React.StrictMode>
        <App />
    </React.StrictMode>
);
